import { henSiteBrowserUrl } from '@highereducation/request-context';
import type { LeadSubmitResponse } from '@highereducation/delivery-system';
import type {
  ConversionV2CreationRequest,
  ConversionV2CreationResponse,
} from '@highereducation/campaign-management';
import { getApiConfig } from '../../services/api/utils';
import { createServiceTrackedHeaders } from '../createServiceTrackedHeaders';
import { newRelicNoticeError } from '../newRelicActions';
import { v4 as uuid } from 'uuid';
import type { BuildLeadObjectInputs } from '../buildLeadObject';
import type { LeadSubmissionConfig } from '../../types/leadSubmission';
import type { Result } from '../../types/result';

// Trailing slashes are required: hen-site runs with `trailingSlash: true`, so a
// request to the slash-less path is 308-redirected to the canonical
// trailing-slash form. Calling the canonical path directly avoids that hop.
const CONVERSION_INIT_PATH =
  '/api/hen/v1/delivery-service/lead/conversion-init/';
const SUBMIT_PATH = '/api/hen/v1/delivery-service/lead/submit/';
const QUALIFICATIONS_NOT_MET = 'qualifications_not_met';
const VOYAGER_EXPERIENCE = 'Voyager';

type SubmitLeadParams = {
  activeProspectToken: string;
  record: LeadSubmissionConfig['record'];
  result: Result;
  conversionId: string;
  inputs: BuildLeadObjectInputs;
  productCorrelationId: string;
  contactId?: string;
  jornayaToken: string;
  needsActiveProspectToken: boolean;
  identityToken?: string;
  monetizationChannel?: string;
};

/**
 * The request body the browser sends to the same-origin lead `submit` BFF
 * route. The server fetches the LDS v4 schema, builds the lead, assigns the
 * `thirdPartyLeadId`, submits to LDS v4, and completes the conversion — all the
 * pieces that require server-set headers (`x-forwarded-for`, eventing) live
 * server-side. Only the `window`-derived data is gathered here and forwarded.
 */
type SubmitLeadRequestBody = {
  organizationId: string;
  offeringUuid?: string;
  slug: string;
  conversionId: string;
  inputs: BuildLeadObjectInputs;
  leadSource: LeadSubmissionConfig['record']['program']['leadSource'];
  needsActiveProspectToken: boolean;
  activeProspectToken: string;
  jornayaToken: string;
  trackingContext: {
    correlationId: string;
    publisher: string;
    anonymousId?: string;
    url?: string;
    experience: string;
    monetizationChannel: string;
  };
  messageContext?: { isSync: boolean };
  identityToken?: string;
};

function leadApiBase(): string {
  return getApiConfig().leadApiBaseUrl ?? '';
}

/**
 * Reads a `qualifications_not_met` signal out of a BFF error response so the
 * client can rethrow the recognizable error the UI maps to a friendly message.
 */
function isQualificationsNotMet(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const record = payload as Record<string, unknown>;
  const error = typeof record.error === 'string' ? record.error : '';
  const message = typeof record.message === 'string' ? record.message : '';
  return (
    error.includes(QUALIFICATIONS_NOT_MET) ||
    message.includes(QUALIFICATIONS_NOT_MET)
  );
}

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Report a lead-delivery failure to browser New Relic. The noticed error is
 * status and reason only: response bodies can carry lead fields, so they stay
 * on the thrown error and the console, not on the New Relic event.
 */
function reportLeadDeliveryError(
  error: unknown,
  source: 'submitLead' | 'initConversion',
  reason: string,
  status?: number,
): void {
  newRelicNoticeError(
    error instanceof Error ? error : new Error(String(error)),
    {
      source,
      reason,
      ...(status !== undefined ? { status } : {}),
    },
  );
}

/**
 * Initializes a conversion via the same-origin campaign-management BFF route.
 * The server sets the real client IP (`x-forwarded-for`) and forwards eventing
 * headers the browser cannot set; the browser only attaches the service-tracked
 * eventing headers it owns.
 */
export const initConversion = async (
  conversion: ConversionV2CreationRequest,
): Promise<ConversionV2CreationResponse> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...createServiceTrackedHeaders(uuid()),
  };

  let httpStatus: number | undefined;
  try {
    const response = await fetch(
      henSiteBrowserUrl(leadApiBase(), CONVERSION_INIT_PATH),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(conversion),
      },
    );

    if (!response.ok) {
      httpStatus = response.status;
      const payload = await readJsonSafe(response);
      throw new Error(
        `Failed to initialize conversion (status ${response.status}): ${JSON.stringify(
          payload,
        )}`,
      );
    }

    return (await response.json()) as ConversionV2CreationResponse;
  } catch (error) {
    console.error('Could not initialize conversion', error);
    reportLeadDeliveryError(
      httpStatus === undefined
        ? error
        : new Error(`Failed to initialize conversion (status ${httpStatus})`),
      'initConversion',
      httpStatus === undefined ? 'request-failed' : 'http-error',
      httpStatus,
    );
    throw error;
  }
};

/**
 * Submits a lead through the same-origin delivery-service BFF route, which
 * fetches the LDS v4 schema, builds + submits the lead, then completes the
 * conversion server-side. Rethrows `qualifications_not_met` so the caller can
 * surface the friendly profile-mismatch message.
 */
export const submitLead = async ({
  needsActiveProspectToken,
  activeProspectToken,
  record,
  result,
  conversionId,
  inputs,
  productCorrelationId,
  jornayaToken,
  identityToken,
  monetizationChannel,
}: SubmitLeadParams): Promise<LeadSubmitResponse> => {
  if (!inputs) {
    const error = new Error('Invalid body');
    reportLeadDeliveryError(error, 'submitLead', 'invalid-body');
    throw error;
  }

  const organizationId = result.organizationId?.trim();
  if (!organizationId) {
    const error = new Error(
      'LDS v4 organizationId is required to submit a lead',
    );
    reportLeadDeliveryError(error, 'submitLead', 'missing-organization-id');
    throw error;
  }
  const offeringUuid = result.offeringUuid?.trim();
  const normalizedMonetizationChannel = monetizationChannel?.trim();
  const resolvedMonetizationChannel = normalizedMonetizationChannel
    ? normalizedMonetizationChannel
    : 'voyager';

  // Surface a missing Cohesion anonymousId as telemetry instead of silently
  // sending `undefined`: the lead still submits (fail-open), but downstream
  // identity stitching loses the pre-auth anonymousId, so the gap is worth a
  // signal in New Relic.
  const anonymousId = (window as any)?._Cohesion?.anonymousId;
  if (!anonymousId) {
    newRelicNoticeError(
      new Error(
        'submitLead: missing Cohesion anonymousId; submitting lead without it',
      ),
      { source: 'submitLead', reason: 'missing-anonymous-id' },
    );
  }

  const body: SubmitLeadRequestBody = {
    organizationId,
    ...(offeringUuid ? { offeringUuid } : {}),
    slug: result.schoolSlug,
    conversionId,
    inputs,
    leadSource: record.program.leadSource,
    needsActiveProspectToken,
    activeProspectToken,
    jornayaToken,
    trackingContext: {
      correlationId: productCorrelationId,
      publisher:
        new URLSearchParams(window.location.search).get('publisher') ||
        window.location.host,
      anonymousId,
      url: inputs?.sourceUrl?.value || window.location.href,
      experience: VOYAGER_EXPERIENCE,
      monetizationChannel: resolvedMonetizationChannel,
    },
    ...(result.synchronousLeadDelivery === true && {
      messageContext: { isSync: true },
    }),
    ...(identityToken ? { identityToken } : {}),
  };

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...createServiceTrackedHeaders(uuid()),
  };

  let response: Response;
  try {
    response = await fetch(henSiteBrowserUrl(leadApiBase(), SUBMIT_PATH), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  } catch (error) {
    console.error('Could not submit lead', error);
    reportLeadDeliveryError(error, 'submitLead', 'network-error');
    throw error;
  }

  if (!response.ok) {
    const payload = await readJsonSafe(response);
    if (isQualificationsNotMet(payload)) {
      const error = new Error(QUALIFICATIONS_NOT_MET);
      reportLeadDeliveryError(
        error,
        'submitLead',
        'qualifications-not-met',
        response.status,
      );
      throw error;
    }
    console.error('Could not submit lead', payload);
    reportLeadDeliveryError(
      new Error(`Failed to submit lead (status ${response.status})`),
      'submitLead',
      'http-error',
      response.status,
    );
    throw new Error(
      `Failed to submit lead (status ${response.status}): ${JSON.stringify(
        payload,
      )}`,
    );
  }

  const leadResponse = (await response.json()) as LeadSubmitResponse;
  // Defense-in-depth: the BFF submit route already returns a 500 whenever LDS
  // reports `leadSubmitted: false`, so the `!response.ok` branch above catches
  // that path first and this guard is unreachable on the normal flow. It is
  // kept intentionally to cover a hypothetical 200-with-false-flag regression
  // in the route.
  if (leadResponse.leadSubmitted !== true) {
    const error = new Error('LDS did not accept the lead');
    reportLeadDeliveryError(error, 'submitLead', 'lead-not-accepted');
    throw error;
  }

  return leadResponse;
};
